import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../Store/store';
import * as Lucide from 'lucide-react';
import { motion } from 'framer-motion';

interface ScreenTitleProps {
    title: string;
    icon?: React.ReactNode;
    onBack?: () => void;
    animationDelay?: number;
    showBackButton?: boolean;
    children?: React.ReactNode;
}

/**
 * Common screen title component that can be used across all pages.
 * Gets game settings from Redux instead of requiring them as props.
 * Supports navigation back using the page navigation system.
 */
const ScreenTitle: React.FC<ScreenTitleProps> = ({ 
    title, 
    icon, 
    onBack,
    animationDelay = 0,
    showBackButton = true,
    children
}) => {
    const { currentGame } = useSelector((state: RootState) => state.gameSession);
    
    if (!currentGame) {
        // Fallback if Redux isn't initialized yet
        return (
            <div style={{ 
                textAlign: 'center', 
                padding: '1rem',
                color: 'white',
                fontSize: '1.5rem',
                fontWeight: 'bold'
            }}>
                {title}
            </div>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: animationDelay }}
            style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '1rem',
                marginTop: '0.5rem',
                userSelect: 'none',
                zIndex: 1000,
            }}
        >
            {/* Title row with back button, icon, and text */}
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '0.5rem',
                    cursor: showBackButton && onBack ? 'pointer' : 'default',
                }}
                onClick={showBackButton && onBack ? onBack : undefined}
            >
                {/* Back chevron */}
                {showBackButton && onBack && (
                    <Lucide.ChevronLeft 
                        size={30} 
                        strokeWidth={4} 
                        color={currentGame.colorPrimary}
                        style={{ filter: 'drop-shadow(1px 1px 2px rgba(0, 0, 0, 0.5))' }}
                    />
                )}
                
                {/* Leading icon */}
                {icon && <div style={{ color: 'rgba(255, 255, 255, 1)', marginTop: '0.2rem' }}>
                    {icon}
                </div>}
                
                {/* Title text */}
                <span style={{
                    color: 'rgba(255, 255, 255, 1)',
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    fontFamily: currentGame.fontPrimary,
                    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)',
                }}>
                    {title}
                </span>
            </div>
            
            {/* Children content */}
            {children && (
                <div style={{ 
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '0.5rem'
                }}>
                    {children}
                </div>
            )}
        </motion.div>
    );
};

export default ScreenTitle;
